// What the camera follows: a small interface, so a scripted lab target and the real hero plug in alike. The sim side
// pushes the target's state once per sim step; the view side samples it at the frame's interpolation alpha. The
// camera never reads the newest step directly (that judders on frames that took no step).
import type { Vec3 } from "./math";

/** The target as the camera sees it on one frame. */
export interface TargetSample {
  /** Position (the feet or the body's origin; the profile's pivotHeight is added on top), interpolated. */
  position: Vec3;
  /** Velocity (m/s), interpolated. */
  velocity: Vec3;
  /** The yaw the target faces (rad, the camera's convention), or null to use its velocity's heading. */
  facing: number | null;
  /** On the ground (the vertical follow is slower in the air). */
  grounded: boolean;
}

/** Anything the camera can follow. */
export interface CameraTarget {
  /** The target's state at `alpha` between the previous sim step (0) and the newest (1). */
  sample(alpha: number): TargetSample;
  /** Bumped when the target teleports, so the camera cuts instead of following across the jump. */
  readonly cuts?: number;
}

/** One sim step's state of a target. */
export interface TargetState {
  position: Vec3;
  velocity: Vec3;
  facing?: number | null;
  grounded?: boolean;
}

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
const lerpV = (a: Vec3, b: Vec3, t: number): Vec3 => ({ x: lerp(a.x, b.x, t), y: lerp(a.y, b.y, t), z: lerp(a.z, b.z, t) });
const lerpYaw = (a: number, b: number, t: number): number => a + Math.atan2(Math.sin(b - a), Math.cos(b - a)) * t;

/**
 * A target the sim writes once per step (`push`) and the camera samples between steps. `teleport` moves it with no
 * interpolation from where it was and bumps `cuts`.
 */
export class SteppedTarget implements CameraTarget {
  private prev: Required<TargetState>;
  private curr: Required<TargetState>;
  /** Teleports so far. */
  cuts = 0;

  constructor(initial: TargetState) {
    this.curr = full(initial);
    this.prev = full(initial);
  }

  /** The newest step's state (call once per sim step, after the target moved). */
  push(s: TargetState): void {
    this.prev = this.curr;
    this.curr = full(s);
  }

  /** Move with no interpolation (a warp, a respawn, a restored save). */
  teleport(s: TargetState): void {
    this.curr = full(s);
    this.prev = full(s);
    this.cuts++;
  }

  /** The newest step's state. */
  get latest(): Readonly<Required<TargetState>> {
    return this.curr;
  }

  sample(alpha: number): TargetSample {
    const a = Math.min(1, Math.max(0, alpha));
    const fa = this.prev.facing;
    const fb = this.curr.facing;
    return {
      position: lerpV(this.prev.position, this.curr.position, a),
      velocity: lerpV(this.prev.velocity, this.curr.velocity, a),
      facing: fa === null || fb === null ? fb : lerpYaw(fa, fb, a),
      grounded: a < 0.5 ? this.prev.grounded : this.curr.grounded,
    };
  }
}

function full(s: TargetState): Required<TargetState> {
  return { position: { ...s.position }, velocity: { ...s.velocity }, facing: s.facing ?? null, grounded: s.grounded ?? true };
}
